import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query'
import { PackageOpen, Printer } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import {
  EXCHANGE_REASON_CODES,
  EXCHANGE_REASON_LABELS,
  NO_REASON_LABEL,
  RETURNED_PIECES_COLUMNS,
  type ExchangeReasonCode,
  type ReportColumn,
  type ReturnedPieceRowType,
} from '@aeris/contracts'
import { reportPrintFor, type PrintDocument } from '@aeris/printing'
import {
  Button,
  EmptyState,
  Pagination,
  SearchInput,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@aeris/ui'

import { fetchBrands } from '../lib/api/brands.ts'
import { fetchReturnedPieces } from '../lib/api/reports.ts'
import {
  dateValue,
  numberValue,
  textValue,
  type ExportColumn,
} from '../lib/export/table-export.ts'
import { useDebounced } from '../lib/use-debounced.ts'
import {
  useCompanyHeader,
  useReceiptTemplate,
} from '../lib/use-receipt-template.ts'

import { ExportMenu } from './export-menu.tsx'
import { PrintDocumentDialog } from './print-document-dialog.tsx'

/**
 * As peças devolvidas, uma por linha — F178.
 *
 * O dono pediu com estas palavras: *"no relatório de defeito tem que aparecer
 * o motivo, porque vai que eu quero tirar o relatório de todas as telas para
 * saber o motivo de cada uma que voltou — Elias, tela X, defeito: atualização
 * parou o touch"*.
 *
 * ## Por que embaixo do resumo, e não no lugar dele
 *
 * O relatório agregado responde "onde olhar" (defeito é 60% do que volta); esta
 * lista responde "o que aconteceu" (parou o touch, três vezes no mesmo modelo).
 * São perguntas diferentes e a segunda não cabe num ranking — uma linha por
 * peça, com a frase que o trocador escreveu, é o formato.
 *
 * As colunas vêm de `RETURNED_PIECES_COLUMNS`: a tela e o Excel leem a mesma
 * lista, para uma coluna nova nunca aparecer só num dos dois.
 */

const PAGE_SIZE = 50

/** A página do Excel: o servidor entrega no máximo 200 por vez. */
const EXPORT_PAGE_SIZE = 200

/**
 * O teto de páginas que o papel percorre — F188.
 *
 * 200 × 50 = 10 000 peças, que é mais do que uma loja devolve num ano. Existe
 * para um defeito de paginação não virar laço infinito, e não para cortar
 * relatório de ninguém: com um filtro razoável ele nunca é alcançado.
 */
const PRINT_PAGE_CAP = 50

/** `2026-09-01T00:00Z` → `01/09/2026`. */
const dayFormat = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' })
const dayLabel = (iso: string | null): string =>
  iso === null ? '—' : dayFormat.format(new Date(iso))

/** "01/09/2026 a 30/09/2026" — o fim é EXCLUSIVO, então volta um dia. */
const periodLabel = (from: string, to: string): string => {
  const end = new Date(to)
  end.setDate(end.getDate() - 1)
  return `${dayLabel(from)} a ${dayFormat.format(end)}`
}

const money = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
})

const shortDate = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' })

/** O mesmo valor, escrito para gente ler na tela. */
const cell = (
  kind: ReportColumn['kind'],
  raw: string | null | undefined
): string => {
  if (raw === null || raw === undefined || raw === '') return '—'
  switch (kind) {
    case 'money':
      return money.format(Number(raw))
    case 'quantity':
      return raw.replace(/\.?0+$/, '').replace('.', ',')
    case 'date':
      return shortDate.format(new Date(raw))
    case 'percent':
      return `${raw.replace('.', ',')}%`
    case 'count':
    case 'text':
      return raw
  }
}

const exportColumns: readonly ExportColumn<ReturnedPieceRowType>[] =
  RETURNED_PIECES_COLUMNS.map((column) => ({
    id: column.id,
    label: column.label,
    value: (row) => {
      const raw = row.values[column.id]
      if (raw === null || raw === undefined) return null
      switch (column.kind) {
        case 'money':
        case 'quantity':
        case 'count':
        case 'percent':
          return numberValue(raw)
        case 'date':
          return dateValue(raw)
        case 'text':
          return textValue(raw)
      }
    },
  }))

export type ReturnedPiecesPanelProps = {
  /** Início inclusivo e fim EXCLUSIVO, os mesmos do relatório de cima. */
  readonly from: string
  readonly to: string
  readonly branchId?: string | undefined
}

export const ReturnedPiecesPanel = ({
  from,
  to,
  branchId,
}: ReturnedPiecesPanelProps): ReactNode => {
  const [reasonCode, setReasonCode] = useState<ExchangeReasonCode | ''>('')
  /* A marca, separada da busca livre — F188. Ver o comentário do contrato. */
  const [brand, setBrand] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const term = useDebounced(search.trim())

  const filter = {
    from,
    to,
    ...(branchId === undefined || branchId === '' ? {} : { branchId }),
    ...(reasonCode === '' ? {} : { reasonCode }),
    ...(brand === '' ? {} : { brand }),
    ...(term === '' ? {} : { search: term }),
  }

  const query = useQuery({
    queryKey: ['returned-pieces', filter, page],
    queryFn: async ({ signal }) =>
      await fetchReturnedPieces(
        { ...filter, page, pageSize: PAGE_SIZE },
        signal
      ),
    /*
     * A lista anterior fica enquanto a nova chega: trocar de página com o
     * período aberto pisca menos, e o trocador não perde a linha que estava
     * lendo.
     */
    placeholderData: keepPreviousData,
  })

  const data = query.data
  const rows = data?.items ?? []
  const total = data?.total ?? 0

  /* As mesmas marcas do cadastro, e o mesmo cache do Estoque e de Produtos. */
  const brandQuery = useQuery({
    queryKey: ['brands', 'active'],
    queryFn: ({ signal }) => fetchBrands('active', signal),
  })
  const brands = (brandQuery.data ?? []).map((entry) => entry.name)

  const company = useCompanyHeader()
  const template = useReceiptTemplate()
  const [printable, setPrintable] = useState<PrintDocument | null>(null)

  /*
   * O papel lê TUDO o que o filtro achou, não a página — F188.
   *
   * O Eduardo vai à Samsung com as peças da Samsung do mês, e são 80 linhas,
   * não as 50 que couberam na tela. Caminha de 200 em 200 pela mesma rota da
   * lista, como a exportação já faz, então a permissão é a mesma e não há
   * porta por onde saia mais do que a tela mostra.
   */
  const printReport = useMutation({
    mutationFn: async () => {
      const collected: ReturnedPieceRowType[] = []
      let wanted = 1
      for (;;) {
        const response = await fetchReturnedPieces({
          ...filter,
          page: wanted,
          pageSize: EXPORT_PAGE_SIZE,
        })
        collected.push(...response.items)
        if (
          response.items.length === 0 ||
          collected.length >= response.total ||
          wanted > PRINT_PAGE_CAP
        ) {
          break
        }
        wanted += 1
      }

      const label = (id: string): string =>
        RETURNED_PIECES_COLUMNS.find((column) => column.id === id)?.label ?? id

      return reportPrintFor({
        company,
        template,
        title: 'Peças devolvidas',
        filters: [
          { label: 'Período', value: periodLabel(from, to) },
          {
            label: 'Motivo',
            value:
              reasonCode === '' ? 'Todos' : EXCHANGE_REASON_LABELS[reasonCode],
          },
          { label: 'Marca', value: brand === '' ? 'Todas' : brand },
          { label: 'Peças', value: String(collected.length) },
        ],
        /*
         * O motivo é a PRIMEIRA coluna depois do nome da peça: é a razão de o
         * papel existir. `reportPrintFor` põe a primeira em destaque e as
         * outras embaixo, rotuladas.
         */
        columns: [
          { label: label('reason') },
          { label: label('date') },
          { label: label('brand') },
          { label: label('sku') },
          { label: label('note') },
          { label: label('handledBy') },
          { label: label('customer') },
        ],
        rows: collected.map((row) => ({
          label: row.values.product ?? '—',
          values: [
            row.values.reason ?? NO_REASON_LABEL,
            dayLabel(row.values.date ?? null),
            row.values.brand ?? '—',
            row.values.sku ?? '—',
            row.values.note ?? '—',
            row.values.handledBy ?? '—',
            row.values.customer ?? '—',
          ],
        })),
        others: null,
        /*
         * Só o primeiro valor: `reportPrintFor` escreve os demais como
         * "Rótulo: valor" embaixo, e com strings vazias o papel saía com uma
         * linha de "Data: · Marca: · Código:" sem nada depois dos dois pontos.
         */
        total: { label: 'Total de peças', values: [String(collected.length)] },
      })
    },
    onSuccess: setPrintable,
  })

  return (
    <section className="flex flex-col gap-3" aria-label="Peças devolvidas">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-(length:--text-sm) font-(--weight-semibold)">
          Peça por peça, com o motivo de cada uma
        </h2>
        <div className="flex items-center gap-2">
          {/*
            O papel do fornecedor — F188.

            O Eduardo: *"pra imprimir o relatório e levar pros fornecedores
            listando cada motivo de cada peça"*. Até aqui a única saída era
            Excel, e ninguém leva planilha para uma conversa de balcão com
            representante. É o MESMO `reportPrintFor` de Relatórios e
            Comissões, com o cabeçalho da loja e os filtros aplicados escritos
            no topo — senão o papel não diz de que período ele é.
          */}
          <Button
            variant="secondary"
            size="sm"
            loading={printReport.isPending}
            disabled={total === 0}
            leadingIcon={<Printer aria-hidden="true" className="h-4 w-4" />}
            onClick={() => {
              printReport.mutate()
            }}
          >
            Imprimir
          </Button>
          <ExportMenu
            fileName="pecas-devolvidas"
            sheetName="Peças devolvidas"
            columns={exportColumns}
            fetchPage={async (wanted) => {
              const response = await fetchReturnedPieces({
                ...filter,
                page: wanted,
                pageSize: EXPORT_PAGE_SIZE,
              })
              return { items: response.items, total: response.total }
            }}
            pageSize={EXPORT_PAGE_SIZE}
            total={total}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select
          aria-label="Motivo"
          value={reasonCode}
          className="w-48"
          onChange={(event) => {
            setReasonCode(event.target.value as ExchangeReasonCode | '')
            setPage(1)
          }}
        >
          <option value="">Todos os motivos</option>
          {EXCHANGE_REASON_CODES.map((code) => (
            <option key={code} value={code}>
              {EXCHANGE_REASON_LABELS[code]}
            </option>
          ))}
        </Select>
        {brands.length === 0 ? null : (
          <Select
            aria-label="Marca"
            value={brand}
            className="w-48"
            onChange={(event) => {
              setBrand(event.target.value)
              setPage(1)
            }}
          >
            <option value="">Todas as marcas</option>
            {brands.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </Select>
        )}
        <SearchInput
          aria-label="Buscar peça, cliente ou código"
          placeholder="produto, marca, código ou cliente"
          value={search}
          className="w-64"
          onChange={(event) => {
            setSearch(event.target.value)
            setPage(1)
          }}
        />
      </div>

      {printable === null ? null : (
        <PrintDocumentDialog
          document={printable}
          description="Peças devolvidas"
          initialPaper="a4"
          onClose={() => {
            setPrintable(null)
          }}
        />
      )}

      {rows.length === 0 && !query.isPending ? (
        <EmptyState
          icon={<PackageOpen aria-hidden="true" className="h-8 w-8" />}
          title="Nenhuma peça voltou neste filtro"
          description="Mude o período, o motivo ou a busca."
        />
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {RETURNED_PIECES_COLUMNS.map((column) => (
                    <TableHeaderCell
                      key={column.id}
                      numeric={column.kind !== 'text' && column.kind !== 'date'}
                    >
                      {column.label}
                    </TableHeaderCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    {RETURNED_PIECES_COLUMNS.map((column) => (
                      <TableCell
                        key={column.id}
                        numeric={
                          column.kind !== 'text' && column.kind !== 'date'
                        }
                      >
                        {cell(column.kind, row.values[column.id])}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Pagination
            page={page}
            pageSize={PAGE_SIZE}
            total={total}
            onPageChange={setPage}
          />
        </>
      )}
    </section>
  )
}
